import { useEffect, useState } from 'react';
import { helpdeskApi, type HelpdeskCardKind, type HelpdeskCardTarget } from '../api/helpdesk';

const KIND_LABEL: Record<HelpdeskCardKind, string> = {
  klient: 'Klient',
  affiliate: 'Affiliate partner',
};

export function AddContactToCard({
  ticketId,
  email,
  defaultName,
  onSaved,
}: {
  ticketId: string;
  email: string;
  defaultName: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<HelpdeskCardKind>('klient');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<HelpdeskCardTarget[]>([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState<HelpdeskCardTarget | null>(null);
  const [name, setName] = useState(defaultName);
  const [position, setPosition] = useState('');
  const [vocative, setVocative] = useState('');
  const [vocativeEdited, setVocativeEdited] = useState(false);
  const [vocativeLoading, setVocativeLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const term = query.trim();
    if (!open || target || term.length < 2) { setHits([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      helpdeskApi.cardTargets(ticketId, term)
        .then((r) => { if (alive) setHits(r); })
        .catch(() => { if (alive) setHits([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [open, query, target, ticketId]);

  useEffect(() => {
    const term = name.trim();
    if (!open || vocativeEdited) return;
    if (term.length < 2) { setVocative(''); return; }
    let alive = true;
    setVocativeLoading(true);
    const t = setTimeout(() => {
      helpdeskApi.suggestVocative(ticketId, term)
        .then((r) => { if (alive) setVocative(r.vocative); })
        .catch(() => { if (alive) setVocative(''); })
        .finally(() => { if (alive) setVocativeLoading(false); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [open, name, vocativeEdited, ticketId]);

  const visible = hits.filter((h) => h.kind === kind);

  const reset = () => {
    setOpen(false); setQuery(''); setHits([]); setTarget(null);
    setName(defaultName); setPosition(''); setVocative(''); setVocativeEdited(false); setError('');
  };

  const save = async () => {
    if (!target) { setError(`Vyber ${kind === 'klient' ? 'klienta' : 'affiliate partnera'}.`); return; }
    if (name.trim().length < 2) { setError('Vyplň jméno kontaktní osoby.'); return; }
    if (!vocative.trim()) { setError('Vyplň oslovení (5. pád), např. Adélo.'); return; }
    setSaving(true); setError('');
    try {
      await helpdeskApi.addToCard(ticketId, { kind, targetId: target.id, name: name.trim(), vocative: vocative.trim(), position: position.trim() });
      reset();
      onSaved();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Uložení se nezdařilo.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="hd-card-add-btn" onClick={() => setOpen(true)}>
        + Přidat na kartu
      </button>
    );
  }

  return (
    <div className="hd-card-add">
      <div className="hd-card-kinds" role="radiogroup" aria-label="Kam kontakt přidat">
        {(Object.keys(KIND_LABEL) as HelpdeskCardKind[]).map((k) => (
          <label key={k} className={kind === k ? 'active' : ''}>
            <input
              type="radio"
              name={`card-kind-${ticketId}`}
              checked={kind === k}
              onChange={() => { setKind(k); setTarget(null); setError(''); }}
            />
            {KIND_LABEL[k]}
          </label>
        ))}
      </div>

      {target ? (
        <div className="hd-card-target">
          <span><strong>{target.name}</strong>{target.detail && <small>{target.detail}</small>}</span>
          <button type="button" onClick={() => { setTarget(null); setQuery(''); }} aria-label="Změnit výběr">×</button>
        </div>
      ) : (
        <div className="hd-card-search">
          <input
            className="form-control form-control-sm"
            placeholder={kind === 'klient' ? 'Hledej klienta…' : 'Hledej affiliate partnera…'}
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.trim().length >= 2 && (
            <ul className="hd-card-hits">
              {searching && visible.length === 0 && <li className="hd-card-hint">Hledám…</li>}
              {!searching && visible.length === 0 && <li className="hd-card-hint">Nic nenalezeno.</li>}
              {visible.map((h) => (
                <li key={`${h.kind}-${h.id}`}>
                  <button type="button" onClick={() => { setTarget(h); setError(''); }}>
                    <strong>{h.name}</strong>
                    {h.detail && <small>{h.detail}</small>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <label className="hd-card-field">
        <span>Jméno a příjmení</span>
        <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="hd-card-field">
        <span>Oslovení (5. pád) *</span>
        <input
          className="form-control form-control-sm"
          value={vocative}
          placeholder={vocativeLoading ? 'Skloňuji…' : 'např. Adélo'}
          onChange={(e) => { setVocative(e.target.value); setVocativeEdited(true); }}
        />
      </label>
      <label className="hd-card-field">
        <span>Pozice</span>
        <input className="form-control form-control-sm" value={position} onChange={(e) => setPosition(e.target.value)} />
      </label>
      <div className="hd-card-email">{email}</div>

      {error && <div className="hd-card-error">{error}</div>}
      <div className="hd-card-actions">
        <button type="button" className="hd-btn-send2" onClick={reset} disabled={saving}>Zrušit</button>
        <button type="button" className="hd-btn-send" onClick={save} disabled={saving || !target || !vocative.trim()}>
          {saving ? 'Ukládám…' : 'Uložit'}
        </button>
      </div>
    </div>
  );
}
